import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useProgress } from "../context/ProgressContext";
import { Target, Flame, Trophy, Footprints, Plus, Trash2, TrendingUp, Repeat } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const QUESTIONS = [
  { key: "focus", label: "Today's Focus", hint: "What one skill will you improve?", icon: Target },
  { key: "challenge", label: "Today's Challenge", hint: "What will you push yourself to complete?", icon: Flame },
  { key: "result", label: "Today's Result", hint: "What did you achieve and learn?", icon: Trophy },
];

export default function InteractiveJournal() {
  const { entries, saveEntries } = useProgress();
  const [date, setDate] = useState(todayStr());
  const [form, setForm] = useState({ focus: "", challenge: "", result: "", weakFoot: 5, reps: "" });

  const sorted = useMemo(() => [...(entries || [])].sort((a, b) => (a.date < b.date ? 1 : -1)), [entries]);
  const chartData = useMemo(() => [...(entries || [])].sort((a, b) => (a.date < b.date ? -1 : 1)).slice(-10), [entries]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.focus && !form.challenge && !form.result) {
      toast.error("Fill in at least one reflection to log your day.");
      return;
    }
    const entry = { id: `${date}-${Date.now()}`, date, ...form, reps: Math.max(0, parseInt(form.reps, 10) || 0) };
    const withoutSameDay = (entries || []).filter((e) => e.date !== date);
    saveEntries([...withoutSameDay, entry]);
    toast.success("Session logged!", { description: "Consistency builds elite players." });
    setForm({ focus: "", challenge: "", result: "", weakFoot: 5, reps: "" });
  };

  const remove = (id) => saveEntries((entries || []).filter((e) => e.id !== id));

  const streak = useMemo(() => {
    const days = new Set((entries || []).map((e) => e.date));
    let count = 0;
    const d = new Date();
    while (days.has(d.toISOString().slice(0, 10))) { count += 1; d.setDate(d.getDate() - 1); }
    return count;
  }, [entries]);

  return (
    <div className="my-8" data-testid="interactive-journal">
      {/* Daily form */}
      <div className="bg-[#141414] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h3 className="font-headings text-2xl uppercase tracking-wide">Daily Training Log</h3>
          <input
            data-testid="journal-date"
            type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)}
            className="bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
          />
        </div>

        <div className="space-y-4">
          {QUESTIONS.map((q) => (
            <div key={q.key}>
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 mb-1.5">
                <q.icon size={14} className="text-[#C41E24]" /> {q.label}
              </label>
              <textarea
                data-testid={`journal-${q.key}`}
                rows={2} value={form[q.key]} onChange={(e) => set(q.key, e.target.value)}
                placeholder={q.hint}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm text-white placeholder:text-white/30 resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
              />
            </div>
          ))}

          {/* Reps today */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 mb-1.5">
              <Repeat size={14} className="text-[#C41E24]" /> Reps Today
              <span className="ml-auto text-[10px] font-normal normal-case tracking-normal text-white/35">Feeds your Live Technical Ticker</span>
            </label>
            <input
              data-testid="journal-reps"
              type="number" min="0" step="1" inputMode="numeric" value={form.reps}
              onChange={(e) => set("reps", e.target.value)}
              placeholder="e.g. 250 touches / reps"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm text-white placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
            />
          </div>

          {/* Weak foot rating */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 mb-2">
              <Footprints size={14} className="text-[#C41E24]" /> Weak-Foot Progress
              <span className="ml-auto text-[#C41E24] font-headings text-lg" data-testid="journal-weakfoot-value">{form.weakFoot}/10</span>
            </label>
            <input
              data-testid="journal-weakfoot"
              type="range" min="1" max="10" value={form.weakFoot}
              onChange={(e) => set("weakFoot", Number(e.target.value))}
              className="w-full accent-[#C41E24]"
            />
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-white/30 mt-1"><span>Developing</span><span>Elite</span></div>
          </div>

          <button
            data-testid="journal-save-btn" onClick={submit}
            className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3 rounded-sm transition-colors"
          >
            <Plus size={16} /> Log Today's Session
          </button>
        </div>
      </div>

      {/* Weak-foot tracker chart */}
      <div className="mt-6 bg-[#141414] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><TrendingUp size={18} className="text-[#C41E24]" /><h3 className="font-subheadings text-sm font-bold uppercase tracking-widest">Weak-Foot Progress Tracker</h3></div>
          {streak > 0 && <span className="text-xs font-bold text-[#C41E24] flex items-center gap-1"><Flame size={13} /> {streak}-day streak</span>}
        </div>
        {chartData.length ? (
          <div className="flex items-end gap-2 h-40" data-testid="weakfoot-chart">
            {chartData.map((e) => (
              <div key={e.id} className="flex-1 flex flex-col items-center gap-1.5 group">
                <div className="w-full bg-[#0d0d0d] rounded-t-sm relative flex items-end" style={{ height: "100%" }}>
                  <motion.div
                    initial={{ height: 0 }} animate={{ height: `${(e.weakFoot || 0) * 10}%` }} transition={{ duration: 0.6, ease: "easeOut" }}
                    className="w-full bg-gradient-to-t from-[#A0161D] to-[#C41E24] rounded-t-sm relative"
                  >
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">{e.weakFoot}</span>
                  </motion.div>
                </div>
                <span className="text-[9px] text-white/40 whitespace-nowrap">{fmtDate(e.date).split(",")[0]}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/40">Log a few sessions and your weak-foot progress will chart here.</p>
        )}
      </div>

      {/* History */}
      {sorted.length > 0 && (
        <div className="mt-6">
          <h3 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/70 mb-3">Journal History</h3>
          <div className="space-y-3" data-testid="journal-history">
            <AnimatePresence>
              {sorted.map((e) => (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
                  className="bg-[#141414] border border-white/10 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-[#C41E24]">{fmtDate(e.date)}</span>
                    <div className="flex items-center gap-3">
                      {e.reps > 0 && <span className="text-xs text-white/50 flex items-center gap-1" data-testid={`journal-reps-${e.id}`}><Repeat size={12} className="text-[#C41E24]" /> {e.reps} reps</span>}
                      <span className="text-xs text-white/50 flex items-center gap-1"><Footprints size={12} className="text-[#C41E24]" /> {e.weakFoot}/10</span>
                      <button onClick={() => remove(e.id)} className="text-white/30 hover:text-[#C41E24] transition-colors" aria-label="Delete entry"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    {QUESTIONS.map((q) => e[q.key] && (
                      <div key={q.key}>
                        <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-0.5">{q.label}</div>
                        <div className="text-white/75">{e[q.key]}</div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
